import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import styles from './RewardsActivityFeed.module.css';
import LevelBadge from './LevelBadge/LevelBadge';
import { getLevel, formatLevelBadge } from '../utils/levels';
import { Trophy, TrendingUp, Star, Award, Zap, Calendar, Heart, MessageCircle, X, Send, CheckCircle } from 'lucide-react';

interface RewardActivity {
  id: number;
  username: string;
  activityType: 'mission_complete' | 'mission_created' | 'mission_verified' | 'level_up' | 'points_earned' | 'milestone';
  points: number;
  currentLevel: number;
  previousLevel?: number;
  missionTitle?: string;
  description: string;
  timestamp: string;
  likeCount?: number;
  userHasLiked?: boolean;
  commentCount?: number;
}

interface Comment {
  id: number;
  activityId: number;
  userId: number;
  username: string;
  comment: string;
  createdAt: string;
}

interface RewardsActivityFeedProps {
  limit?: number;
  showTitle?: boolean;
  compact?: boolean;
}

const RewardsActivityFeed: React.FC<RewardsActivityFeedProps> = ({ 
  limit = 10, 
  showTitle = true,
  compact = false 
}) => {
  const [activities, setActivities] = useState<RewardActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'level_ups' | 'missions'>('all');
  const [personalizationFilter, setPersonalizationFilter] = useState<'all' | 'friends' | 'squad' | 'department'>('all');
  const [commentModal, setCommentModal] = useState<{ activityId: number; username: string } | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchActivities();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, [limit, personalizationFilter]);

  const fetchActivities = async () => {
    try {
      const res = await api.get(`/auth/user/org-points/activity-feed?limit=${limit}&filter=${personalizationFilter}`);
      const activitiesData = res.data || [];
      
      // Fetch like/comment counts for each activity
      const enrichedActivities = await Promise.all(
        activitiesData.map(async (activity: RewardActivity) => {
          try {
            const likeRes = await api.get(`/auth/user/org-points/activity/${activity.id}/likes`);
            const commentRes = await api.get(`/auth/user/org-points/activity/${activity.id}/comments`);
            return {
              ...activity,
              likeCount: likeRes.data.likeCount || 0,
              userHasLiked: likeRes.data.userHasLiked || false,
              commentCount: commentRes.data.length || 0
            };
          } catch {
            return { ...activity, likeCount: 0, userHasLiked: false, commentCount: 0 };
          }
        })
      );
      
      setActivities(enrichedActivities);
    } catch (err) {
      console.error('Failed to fetch reward activities', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (activityId: number) => {
    try {
      const res = await api.post(`/auth/user/org-points/activity/${activityId}/like`);
      
      setActivities(prev => prev.map(a => 
        a.id === activityId 
          ? { ...a, likeCount: res.data.likeCount, userHasLiked: res.data.liked }
          : a
      ));
    } catch (err) {
      console.error('Failed to toggle like', err);
    }
  };

  const openComments = async (activityId: number, username: string) => {
    setCommentModal({ activityId, username });
    setLoadingComments(true);
    try {
      const res = await api.get(`/auth/user/org-points/activity/${activityId}/comments`);
      setComments(res.data || []);
    } catch (err) {
      console.error('Failed to fetch comments', err);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const closeComments = () => {
    setCommentModal(null);
    setComments([]);
    setNewComment('');
  };

  const submitComment = async () => {
    if (!commentModal || !newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      await api.post(
        `/auth/user/org-points/activity/${commentModal.activityId}/comment`,
        JSON.stringify(newComment.trim()),
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      // Refresh comments
      const res = await api.get(`/auth/user/org-points/activity/${commentModal.activityId}/comments`);
      setComments(res.data || []);
      setNewComment('');
      
      // Update comment count
      setActivities(prev => prev.map(a => 
        a.id === commentModal.activityId 
          ? { ...a, commentCount: res.data.length }
          : a
      ));
    } catch (err) {
      console.error('Failed to submit comment', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'mission_complete':
        return <Trophy size={20} className={styles.iconMission} />;
      case 'mission_created':
        return <Star size={20} className={styles.iconPoints} />;
      case 'mission_verified':
        return <CheckCircle size={20} className={styles.iconLevelUp} />;
      case 'level_up':
        return <TrendingUp size={20} className={styles.iconLevelUp} />;
      case 'points_earned':
        return <Star size={20} className={styles.iconPoints} />;
      case 'milestone':
        return <Award size={20} className={styles.iconMilestone} />;
      default:
        return <Zap size={20} />;
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'just now';
  };

  const filteredActivities = activities.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'level_ups') return a.activityType === 'level_up';
    if (filter === 'missions') return a.activityType === 'mission_complete' || a.activityType === 'mission_created' || a.activityType === 'mission_verified';
    return true;
  });

  if (loading) {
    return (
      <div className={styles.container}>
        {showTitle && <h2 className={styles.title}>🏆 Rewards Activity</h2>}
        <div className={styles.loading}>Loading recent activity...</div>
      </div>
    );
  }

  return (
    <div className={compact ? styles.containerCompact : styles.container}>
      {showTitle && (
        <div className={styles.header}>
          <h2 className={styles.title}>🏆 Rewards Activity</h2>
          <p className={styles.subtitle}>See who's earning points and leveling up!</p>
        </div>
      )}

      {!compact && (
        <>
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>View:</label>
              <select 
                className={styles.filterSelect}
                value={personalizationFilter}
                onChange={(e) => setPersonalizationFilter(e.target.value as any)}
              >
                <option value="all">Everyone</option>
                <option value="friends">Friends</option>
                <option value="squad">Squad Members</option>
                <option value="department">My Department</option>
              </select>
            </div>

            <div className={styles.filterButtonGroup}>
              <button 
                className={filter === 'all' ? styles.filterActive : styles.filterButton}
                onClick={() => setFilter('all')}
              >
                All Activity
              </button>
              <button 
                className={filter === 'level_ups' ? styles.filterActive : styles.filterButton}
                onClick={() => setFilter('level_ups')}
              >
                Level Ups
              </button>
              <button 
                className={filter === 'missions' ? styles.filterActive : styles.filterButton}
                onClick={() => setFilter('missions')}
              >
                Missions
              </button>
            </div>
          </div>
        </>
      )}

      <div className={styles.feed}>
        {filteredActivities.length === 0 ? (
          <div className={styles.empty}>No recent activity</div>
        ) : (
          filteredActivities.map((activity) => (
            <div key={activity.id} className={styles.activityCard}>
              <div className={styles.activityIcon}>
                {getActivityIcon(activity.activityType)}
              </div>
              
              <div className={styles.activityContent}>
                <div className={styles.activityHeader}>
                  <div className={styles.activityUser}>
                    <span className={styles.username}>
                      {activity.username}
                      <span className={styles.levelIndicator}> Lv.{activity.currentLevel}</span>
                    </span>
                  </div>
                  <span className={styles.timestamp}>
                    <Calendar size={14} />
                    {getTimeAgo(activity.timestamp)}
                  </span>
                </div>

                <div className={styles.activityDescription}>
                  {activity.activityType === 'level_up' && activity.previousLevel !== undefined && (
                    <span className={styles.levelUpText}>
                      🎉 Leveled up from <strong>{formatLevelBadge(activity.previousLevel)}</strong> to <strong>{formatLevelBadge(activity.currentLevel)}</strong>!
                    </span>
                  )}
                  
                  {activity.activityType === 'mission_complete' && (
                    <span>
                      ✅ Completed mission: <strong>{activity.missionTitle}</strong>
                    </span>
                  )}

                  {activity.activityType === 'mission_created' && (
                    <span>
                      ⭐ Created mission: <strong>{activity.missionTitle}</strong>
                    </span>
                  )}

                  {activity.activityType === 'mission_verified' && (
                    <span>
                      ✔️ Verified mission: <strong>{activity.missionTitle}</strong>
                    </span>
                  )}

                  {activity.activityType === 'points_earned' && (
                    <span>{activity.description}</span>
                  )}

                  {activity.activityType === 'milestone' && (
                    <span className={styles.milestoneText}>
                      🌟 {activity.description}
                    </span>
                  )}
                </div>

                {activity.points > 0 && (
                  <div className={styles.pointsBadge}>
                    +{activity.points} ORG POINTS
                  </div>
                )}

                {/* Interaction Bar */}
                <div className={styles.interactionBar}>
                  <button 
                    className={`${styles.interactionButton} ${activity.userHasLiked ? styles.liked : ''}`}
                    onClick={() => handleLike(activity.id)}
                  >
                    <Heart size={16} fill={activity.userHasLiked ? 'currentColor' : 'none'} />
                    <span>{activity.likeCount || 0}</span>
                  </button>

                  <button 
                    className={styles.interactionButton}
                    onClick={() => openComments(activity.id, activity.username)}
                  >
                    <MessageCircle size={16} />
                    <span>{activity.commentCount || 0}</span>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Comments Modal */}
      {commentModal && (
        <div className={styles.modalOverlay} onClick={closeComments}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                Comments on {commentModal.username}'s activity
              </h3>
              <button className={styles.modalClose} onClick={closeComments}>
                <X size={20} />
              </button>
            </div>

            <div className={styles.commentsSection}>
              {loadingComments ? (
                <div className={styles.loadingComments}>Loading comments...</div>
              ) : comments.length === 0 ? (
                <div className={styles.noComments}>No comments yet. Be the first to comment!</div>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className={styles.commentItem}>
                    <div className={styles.commentHeader}>
                      <span className={styles.commentUsername}>{comment.username}</span>
                      <span className={styles.commentTime}>{getTimeAgo(comment.createdAt)}</span>
                    </div>
                    <div className={styles.commentText}>{comment.comment}</div>
                  </div>
                ))
              )}
            </div>

            <div className={styles.commentInput}>
              <textarea
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                maxLength={500}
                className={styles.commentTextarea}
                rows={3}
              />
              <div className={styles.commentFooter}>
                <span className={styles.charCount}>{newComment.length}/500</span>
                <button 
                  className={styles.submitButton}
                  onClick={submitComment}
                  disabled={!newComment.trim() || submittingComment}
                >
                  {submittingComment ? 'Posting...' : (
                    <>
                      <Send size={16} />
                      Post Comment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!compact && activities.length >= limit && (
        <div className={styles.viewMore}>
          <button className={styles.viewMoreButton} onClick={() => window.location.href = '/general/org-points'}>
            View Full Activity Feed →
          </button>
        </div>
      )}
    </div>
  );
};

export default RewardsActivityFeed;
